import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { map } from 'rxjs/operators';

import { IAuroraState } from '@apps/aurora.state';
import * as fromBackStore from '@apps/back/store';
import { User } from '@apps/auth/domain/models/back';
import { ApplicationRole } from '@apps/auth/models';
import { DetailedUser } from '@apps/back/store/auth/users/users.common';
import { ImpersonateService } from '@apps/root/services/impersonate.service';
import { PopupMenuRef } from '@ui/misc/popup-menu';
import { PopupMenuConfig } from '@ui/misc/popup-menu/cog/popup-menu.config';
import { PopupMenuService } from '@ui/misc/popup-menu/cog/popup-menu.service';
import { ColoredPopupMenuItem } from '@ui/misc/popup-menu/cog/colored-popup-menu-item.model';
import { BackAuthUsersDeactivateUserRequested, BackAuthUsersActivateUserRequested } from '@apps/back/store/auth/users/global/users-global.actions';

@Component({
    selector: 'aa-user-card',
    templateUrl: './user-card.component.html',
    styleUrls: [
        './user-card.component.scss'
    ],
    standalone: false
})
export class UserCardComponent implements OnInit, OnChanges
{
    @Input() user: User;
    @Input() withRoles: boolean;
    @Input() withImpersonate: boolean = false;
    @Input() cta: (user: User) => void;
    @Input() popupItems: ColoredPopupMenuItem[];
    @Input() impePopin: boolean = false;
    @Output() forceClose: EventEmitter<void> = new EventEmitter<void>();
    @ViewChild('actions', { static: false }) actionsEltRef: ElementRef;

    public mailto: string;
    public tel: string;
    public telto: string;
    public appRoles: ApplicationRole[] = [];
    public isSelected: Observable<boolean> = of(false);
    public popup: PopupMenuRef;
    public isActive: boolean;
    private destroyator = new Subject();
    private configPopup: PopupMenuConfig = {
        width: 14,
        isColored: true,
        positions: [
            {
                originX: 'start',
                originY: 'center',
                overlayX: 'end',
                overlayY: 'center',
                offsetY: 0,
                offsetX: -7
            },
            {
                originX: 'start',
                originY: 'top',
                overlayX: 'end',
                overlayY: 'center',
                offsetY: 0,
                offsetX: -7
            },
            {
                originX: 'start',
                originY: 'top',
                overlayX: 'end',
                overlayY: 'bottom',
                offsetY: 0,
                offsetX: -7
            }
        ]
    } as PopupMenuConfig;

    constructor (
        private store: Store<IAuroraState>,
        private impersonateService: ImpersonateService,
        private popupMenuService: PopupMenuService
    )
    {
        this.isSelected = this.store.select(fromBackStore.getSelectedUser)
            .pipe(
                map((detailedUser: DetailedUser) =>
                    detailedUser !== null && detailedUser.id === this.user.id
                )
            );
    }

    ngOnInit (): void
    {
        this.isActive = this.user.active;
        this.mailto = `mailto:${this.user.email}`;
        this.tel = this.user.phoneNumber ?? this.user.mobile;
        this.telto = `tel:${this.user.phoneNumber ?? this.user.mobile}`;
        if (this.withRoles && this.user.appRoles)
        {
            this.appRoles = this.user.appRoles
                .filter((role: ApplicationRole) =>
                    role.application !== 'AuthApi'
                )
                .sort((ra: ApplicationRole, rb: ApplicationRole) =>
                    ra.application.localeCompare(rb.application)
                );
        }

        if (this.impePopin && this.user.isExploit)
        {
            this.onClick();
        }
    }

    ngOnChanges (changes: SimpleChanges): void
    {
        if (changes['user'] && !changes['user'].firstChange)
        {
            this.isActive = this.user.active;
        }
    }

    onClick ()
    {
        if (this.cta)
        {
            this.cta(this.user);
        }
    }

    onImpersonate = (event: MouseEvent) =>
    {
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.impersonateService.setImpersonation(this.user);
        this.forceClose.emit();
    };

    actionClick ($event: any): void
    {
        $event.stopPropagation();
        $event.stopImmediatePropagation();

        if (this.actionsEltRef)
        {
            const items = this.buildPopupItems();
            if (!items || items.length === 0)
            {
                return;
            }

            this.popup = this.popupMenuService.create(this.actionsEltRef, this.configPopup, items);
            this.popup.closeEvent
                .pipe(
                    tap(() => {
                        // Handle close
                    }),
                    takeUntil(this.destroyator)
                )
                .subscribe();
        }
    }

    private buildPopupItems(): ColoredPopupMenuItem[]
    {
        if (this.popupItems && this.popupItems.length > 0)
        {
            return this.popupItems;
        }

        return [
            {
                key: 'Désactiver',
                enabled: this.isActive,
                icon: 'fas fa-trash-alt',
                klass: 'danger',
                cta: () =>
                {
                    this.isActive = false;
                    this.store.dispatch(BackAuthUsersDeactivateUserRequested({ user: this.user }));
                }
            } as ColoredPopupMenuItem,
            {
                key: 'Activer',
                enabled: !this.isActive,
                icon: 'fas fa-check',
                klass: 'success',
                cta: () =>
                {
                    this.isActive = true;
                    this.store.dispatch(BackAuthUsersActivateUserRequested({ user: this.user }));
                }
            } as ColoredPopupMenuItem
        ];
    }
}
